import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { CadastroItem, Lancamento } from './financeiroShared';
import * as FinApi from '../../services/FinanceiroService';
import SessionService from '../../services/SessionService';

type FinanceiroPermissions = {
  ownWorkspace: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

type FinanceiroDataContextValue = {
  clientes: CadastroItem[];
  categorias: CadastroItem[];
  planosDeContasEntrada: CadastroItem[];
  planosDeContasSaida: CadastroItem[];
  metodos: CadastroItem[];
  cadastrosLoading: boolean;
  lancamentos: Lancamento[];
  lancamentosLoading: boolean;
  lancamentoSaving: boolean;
  cadastroSaving: boolean;
  error: string | null;
  permissions: FinanceiroPermissions;
  clearError: () => void;
  refreshCadastros: () => Promise<void>;
  refreshLancamentos: () => Promise<void>;
  addCliente: (name: string) => Promise<void>;
  removeCliente: (id: number) => Promise<void>;
  addCategoria: (name: string) => Promise<void>;
  removeCategoria: (id: number) => Promise<void>;
  addPlanoDeContasEntrada: (name: string) => Promise<void>;
  removePlanoDeContasEntrada: (id: number) => Promise<void>;
  addPlanoDeContasSaida: (name: string) => Promise<void>;
  removePlanoDeContasSaida: (id: number) => Promise<void>;
  addMetodo: (name: string) => Promise<void>;
  removeMetodo: (id: number) => Promise<void>;
  updateCadastroNome: (id: number, name: string) => Promise<void>;
  createLancamento: (payload: Omit<Lancamento, 'id'>) => Promise<boolean>;
  createLancamentosBatch: (payloads: Omit<Lancamento, 'id'>[]) => Promise<boolean>;
  updateLancamento: (id: string, payload: Omit<Lancamento, 'id'>) => Promise<boolean>;
  deleteLancamento: (id: string) => Promise<boolean>;
};

const FinanceiroDataContext = createContext<FinanceiroDataContextValue | null>(null);

function extractApiMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const d = err.response?.data as { message?: string } | undefined;
    if (d?.message) return d.message;
    if (err.response?.status === 409) return 'Este item já existe.';
    if (err.response?.status === 403) return 'Sem permissão para esta operação.';
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Erro ao comunicar com o servidor.';
}

const DEFAULT_PERMISSIONS: FinanceiroPermissions = {
  ownWorkspace: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
};

async function fetchPermissions(): Promise<FinanceiroPermissions> {
  try {
    const token = SessionService.getToken();
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!token || !apiUrl) return DEFAULT_PERMISSIONS;
    const res = await fetch(`${apiUrl}/users/profile`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    if (!res.ok) return DEFAULT_PERMISSIONS;
    const json = await res.json().catch(() => null);
    const data = json?.data ?? json;
    if (!data) return DEFAULT_PERMISSIONS;
    const pick = (camel: string, pascal: string, fallback: boolean): boolean => {
      const v = data[camel] ?? data[pascal];
      return typeof v === 'boolean' ? v : fallback;
    };
    return {
      ownWorkspace: pick('financeiroOwnWorkspace', 'FinanceiroOwnWorkspace', false),
      canCreate: pick('financeiroCanCreate', 'FinanceiroCanCreate', false),
      canEdit: pick('financeiroCanEdit', 'FinanceiroCanEdit', false),
      canDelete: pick('financeiroCanDelete', 'FinanceiroCanDelete', false),
    };
  } catch {
    return DEFAULT_PERMISSIONS;
  }
}

export function FinanceiroDataProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [clientes, setClientes] = useState<CadastroItem[]>([]);
  const [categorias, setCategorias] = useState<CadastroItem[]>([]);
  const [planosDeContasEntrada, setPlanosDeContasEntrada] = useState<CadastroItem[]>([]);
  const [planosDeContasSaida, setPlanosDeContasSaida] = useState<CadastroItem[]>([]);
  const [metodos, setMetodos] = useState<CadastroItem[]>([]);
  const [lancamentos, setLancamentos] = useState<Lancamento[]>([]);
  const [cadastrosLoading, setCadastrosLoading] = useState(true);
  const [lancamentosLoading, setLancamentosLoading] = useState(true);
  const [lancamentoSaving, setLancamentoSaving] = useState(false);
  const [cadastroSaving, setCadastroSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<FinanceiroPermissions>(DEFAULT_PERMISSIONS);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    let cancelled = false;
    void fetchPermissions().then((p) => {
      if (!cancelled) setPermissions(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshCadastros = useCallback(async () => {
    setCadastrosLoading(true);
    try {
      const bundle = await FinApi.getCadastros();
      setClientes(bundle.clientes);
      setCategorias(bundle.categorias);
      setPlanosDeContasEntrada(bundle.planosDeContasEntrada);
      setPlanosDeContasSaida(bundle.planosDeContasSaida);
      setMetodos(bundle.metodos);
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
    } finally {
      setCadastrosLoading(false);
    }
  }, []);

  const refreshLancamentos = useCallback(async () => {
    setLancamentosLoading(true);
    try {
      const list = await FinApi.getLancamentos();
      setLancamentos(list);
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
    } finally {
      setLancamentosLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshCadastros();
    void refreshLancamentos();
  }, [refreshCadastros, refreshLancamentos]);

  const addCliente = useCallback(
    async (name: string) => {
      const t = name.trim();
      if (!t) return;
      setCadastroSaving(true);
      try {
        const item = await FinApi.addCadastro(FinApi.FinanceiroCadastroKind.Cliente, t);
        setClientes((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
        setError(null);
      } catch (e) {
        setError(extractApiMessage(e));
        throw e;
      } finally {
        setCadastroSaving(false);
      }
    },
    []
  );

  const removeCliente = useCallback(async (id: number) => {
    setCadastroSaving(true);
    try {
      await FinApi.removeCadastro(id);
      setClientes((prev) => prev.filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const addCategoria = useCallback(async (name: string) => {
    const t = name.trim();
    if (!t) return;
    setCadastroSaving(true);
    try {
      const item = await FinApi.addCadastro(FinApi.FinanceiroCadastroKind.CategoriaJuridicaEntrada, t);
      setCategorias((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const removeCategoria = useCallback(async (id: number) => {
    setCadastroSaving(true);
    try {
      await FinApi.removeCadastro(id);
      setCategorias((prev) => prev.filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const addPlanoDeContasEntrada = useCallback(async (name: string) => {
    const t = name.trim();
    if (!t) return;
    setCadastroSaving(true);
    try {
      const item = await FinApi.addCadastro(FinApi.FinanceiroCadastroKind.PlanoDeContasEntrada, t);
      setPlanosDeContasEntrada((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const removePlanoDeContasEntrada = useCallback(async (id: number) => {
    setCadastroSaving(true);
    try {
      await FinApi.removeCadastro(id);
      setPlanosDeContasEntrada((prev) => prev.filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const addPlanoDeContasSaida = useCallback(async (name: string) => {
    const t = name.trim();
    if (!t) return;
    setCadastroSaving(true);
    try {
      const item = await FinApi.addCadastro(FinApi.FinanceiroCadastroKind.PlanoDeContasSaida, t);
      setPlanosDeContasSaida((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const removePlanoDeContasSaida = useCallback(async (id: number) => {
    setCadastroSaving(true);
    try {
      await FinApi.removeCadastro(id);
      setPlanosDeContasSaida((prev) => prev.filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const addMetodo = useCallback(async (name: string) => {
    const t = name.trim();
    if (!t) return;
    setCadastroSaving(true);
    try {
      const item = await FinApi.addCadastro(FinApi.FinanceiroCadastroKind.MetodoPagamento, t);
      setMetodos((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const removeMetodo = useCallback(async (id: number) => {
    setCadastroSaving(true);
    try {
      await FinApi.removeCadastro(id);
      setMetodos((prev) => prev.filter((x) => x.id !== id));
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, []);

  const updateCadastroNome = useCallback(async (id: number, name: string) => {
    const t = name.trim();
    if (!t) return;
    setCadastroSaving(true);
    try {
      await FinApi.updateCadastro(id, t);
      await refreshCadastros();
      setError(null);
    } catch (e) {
      setError(extractApiMessage(e));
      throw e;
    } finally {
      setCadastroSaving(false);
    }
  }, [refreshCadastros]);

  const createLancamento = useCallback(async (payload: Omit<Lancamento, 'id'>) => {
    setLancamentoSaving(true);
    try {
      await FinApi.createLancamento({
        tipo: payload.tipo,
        data: payload.data,
        vencimento: payload.vencimento,
        dataPagamento: payload.dataPagamento,
        descricao: payload.descricao,
        cliente: payload.cliente,
        responsavel: payload.responsavel,
        categoria: payload.categoria,
        planoDeContas: payload.planoDeContas,
        metodo: payload.metodo,
        valor: payload.valor,
        valorPago: payload.valorPago,
        parcelaAtual: payload.parcelaAtual,
        totalParcelas: payload.totalParcelas,
        anexoNome: payload.anexo,
        anexoBase64: payload.anexoBase64,
        anexoContentType: payload.anexoContentType,
        ...(payload.situacaoEnvio ? { situacao: payload.situacaoEnvio } : {}),
        ...(payload.pagamentos
          ? {
              pagamentos: payload.pagamentos.map((p) => ({
                valor: p.valor,
                data: p.data,
                responsavel: p.responsavel,
                metodo: p.metodo,
              })),
            }
          : {}),
      });
      await refreshLancamentos();
      setError(null);
      return true;
    } catch (e) {
      setError(extractApiMessage(e));
      return false;
    } finally {
      setLancamentoSaving(false);
    }
  }, [refreshLancamentos]);

  const createLancamentosBatch = useCallback(async (payloads: Omit<Lancamento, 'id'>[]) => {
    setLancamentoSaving(true);
    try {
      const apiPayloads = payloads.map((p) => ({
        tipo: p.tipo,
        data: p.data,
        vencimento: p.vencimento,
        dataPagamento: p.dataPagamento,
        descricao: p.descricao,
        cliente: p.cliente,
        responsavel: p.responsavel,
        categoria: p.categoria,
        planoDeContas: p.planoDeContas,
        metodo: p.metodo,
        valor: p.valor,
        valorPago: p.valorPago,
        parcelaAtual: p.parcelaAtual,
        totalParcelas: p.totalParcelas,
        anexoNome: p.anexo,
        anexoBase64: p.anexoBase64,
        anexoContentType: p.anexoContentType,
        ...(p.situacaoEnvio ? { situacao: p.situacaoEnvio } : {}),
        ...(p.pagamentos
          ? {
              pagamentos: p.pagamentos.map((pg) => ({
                valor: pg.valor,
                data: pg.data,
                responsavel: pg.responsavel,
                metodo: pg.metodo,
              })),
            }
          : {}),
      }));
      await FinApi.createLancamentosBatch(apiPayloads);
      await refreshLancamentos();
      setError(null);
      return true;
    } catch (e) {
      setError(extractApiMessage(e));
      return false;
    } finally {
      setLancamentoSaving(false);
    }
  }, [refreshLancamentos]);

  const updateLancamento = useCallback(async (id: string, payload: Omit<Lancamento, 'id'>) => {
    setLancamentoSaving(true);
    try {
      await FinApi.updateLancamento(id, {
        tipo: payload.tipo,
        data: payload.data,
        vencimento: payload.vencimento,
        dataPagamento: payload.dataPagamento,
        descricao: payload.descricao,
        cliente: payload.cliente,
        responsavel: payload.responsavel,
        categoria: payload.categoria,
        planoDeContas: payload.planoDeContas,
        metodo: payload.metodo,
        valor: payload.valor,
        valorPago: payload.valorPago,
        parcelaAtual: payload.parcelaAtual,
        totalParcelas: payload.totalParcelas,
        anexoNome: payload.anexo,
        anexoBase64: payload.anexoBase64,
        anexoContentType: payload.anexoContentType,
        ...(payload.situacaoEnvio ? { situacao: payload.situacaoEnvio } : {}),
        ...(payload.pagamentos
          ? {
              pagamentos: payload.pagamentos.map((p) => ({
                valor: p.valor,
                data: p.data,
                responsavel: p.responsavel,
                metodo: p.metodo,
              })),
            }
          : {}),
      });
      await refreshLancamentos();
      setError(null);
      return true;
    } catch (e) {
      setError(extractApiMessage(e));
      return false;
    } finally {
      setLancamentoSaving(false);
    }
  }, [refreshLancamentos]);

  const deleteLancamento = useCallback(async (id: string) => {
    setLancamentoSaving(true);
    try {
      await FinApi.deleteLancamento(id);
      await refreshLancamentos();
      setError(null);
      return true;
    } catch (e) {
      setError(extractApiMessage(e));
      return false;
    } finally {
      setLancamentoSaving(false);
    }
  }, [refreshLancamentos]);

  const value = useMemo<FinanceiroDataContextValue>(
    () => ({
      clientes,
      categorias,
      planosDeContasEntrada,
      planosDeContasSaida,
      metodos,
      cadastrosLoading,
      lancamentos,
      lancamentosLoading,
      lancamentoSaving,
      cadastroSaving,
      error,
      permissions,
      clearError,
      refreshCadastros,
      refreshLancamentos,
      addCliente,
      removeCliente,
      addCategoria,
      removeCategoria,
      addPlanoDeContasEntrada,
      removePlanoDeContasEntrada,
      addPlanoDeContasSaida,
      removePlanoDeContasSaida,
      addMetodo,
      removeMetodo,
      updateCadastroNome,
      createLancamento,
      createLancamentosBatch,
      updateLancamento,
      deleteLancamento,
    }),
    [
      clientes,
      categorias,
      planosDeContasEntrada,
      planosDeContasSaida,
      metodos,
      cadastrosLoading,
      lancamentos,
      lancamentosLoading,
      lancamentoSaving,
      cadastroSaving,
      error,
      permissions,
      clearError,
      refreshCadastros,
      refreshLancamentos,
      addCliente,
      removeCliente,
      addCategoria,
      removeCategoria,
      addPlanoDeContasEntrada,
      removePlanoDeContasEntrada,
      addPlanoDeContasSaida,
      removePlanoDeContasSaida,
      addMetodo,
      removeMetodo,
      updateCadastroNome,
      createLancamento,
      createLancamentosBatch,
      updateLancamento,
      deleteLancamento,
    ]
  );

  return <FinanceiroDataContext.Provider value={value}>{children}</FinanceiroDataContext.Provider>;
}

export function useFinanceiroData(): FinanceiroDataContextValue {
  const ctx = useContext(FinanceiroDataContext);
  if (!ctx) {
    throw new Error('useFinanceiroData must be used within FinanceiroDataProvider');
  }
  return ctx;
}

export function FinanceiroApiErrorBanner(): React.ReactElement | null {
  const { error, clearError } = useFinanceiroData();
  if (!error) return null;
  return (
    <div className="fin-api-error" role="alert">
      <span className="fin-api-error__text">{error}</span>
      <button type="button" className="fin-btn fin-btn--ghost fin-btn--small" onClick={clearError}>
        Fechar
      </button>
    </div>
  );
}
